const MAX_DIMENSION = 2048;
const QUALITY = 0.85;

export interface CompressImageOptions {
  /** Cap on the longest edge, in px. Never upscales. Defaults to 2048. */
  maxDimension?: number;
  /** JPEG quality, 0–1. Defaults to 0.85. */
  quality?: number;
}

export async function compressImage(
  blob: Blob,
  filename: string,
  { maxDimension = MAX_DIMENSION, quality = QUALITY }: CompressImageOptions = {},
): Promise<Blob> {
  try {
    // `from-image` is the spec default; spelled out because a phone JPEG that
    // relies on its EXIF orientation must not come out sideways.
    const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return blob;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const ext = filename.split(".").pop()?.toLowerCase();
    const type = ext === "png" ? "image/png" : "image/jpeg";

    return await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b ?? blob), type, quality),
    );
  } catch {
    return blob;
  }
}
